import { useAuthenticator } from "@aws-amplify/ui-react"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import LogoutIcon from "@mui/icons-material/Logout"
import {
    TextField,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    useTheme,
    Stack,
} from "@mui/material"
import {
    setUpTOTP,
    verifyTOTPSetup,
    updateMFAPreference,
} from "aws-amplify/auth"
import QRCode from "qrcode"
import React, { ReactElement, useState, useEffect } from "react"

import UnableToLoginHelp from "../../aws/unableToLoginHelp"
import { useAppDispatch } from "../../hooks/useDispatch"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    getShowMFA,
    setShowAccountSetUp,
    setShowMFA,
} from "../../redux/preferencesSlice"
import { CLOSED } from "../../redux/types"
import useRestorePermissions from "../../services/middleware/hooks/adminPortal/useRestorePermissions"
import { scrollToTop } from "../../utils/submitApplication/submitApplicationUtils"
import { BUTTON_SPACING } from "../account/common/config"
import FullScreenTitleContent from "../account/common/fullScreenTitleContent"
import AppButton from "../buttons/appButton"
import AppAlert from "../dataDisplay/alerts/appAlert"
import CheckMarkListWithPrefix from "../dataDisplay/lsits/checkMarkListWithPrefix"
import AppDialog from "../dialog/appDialog"
import AppSkeleton from "../loading/skeletons/appSkeleton"

const QR_CODE_WIDTH = 228

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

export const MFA_MODE_ACCOUNT = "accountDialog"
export const MFA_MODE_MFA_POP_UP = "mfaPopUp"

type MfaMode = typeof MFA_MODE_ACCOUNT | typeof MFA_MODE_MFA_POP_UP

interface MfaPopUpProps {
    onSuccess: () => void
    mode: MfaMode
}

export const MfaPopUp = ({ onSuccess, mode }: MfaPopUpProps): ReactElement => {
    const [isLoading, setIsLoading] = useState(true)
    const [isVerifyingToken, setIsVerifyingToken] = useState(false)
    const [qrCode, setQrCode] = useState("")
    const [token, setToken] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const { groups, email: userEmail } = useGetUserData()
    const { signOut } = useAuthenticator((context) => [context.authStatus])
    const dispatch = useAppDispatch()
    const { restoreUserPermissions } = useRestorePermissions()

    const isMfaResetGroup = groups.includes("MFA-RESET")

    const issuer =
        CONFIG.ENVIRONMENT === "prd"
            ? "ConnectDirect"
            : `ConnectDirect-${CONFIG.ENVIRONMENT}`

    const getTotpCode = (is: string, email: string, secret: string): string =>
        encodeURI(`otpauth://totp/${is}:${email}?secret=${secret}&issuer=${is}`)

    const generateQRCode = React.useCallback(async (): Promise<void> => {
        try {
            const totpSetupDetails = await setUpTOTP()
            const secretKey = totpSetupDetails.sharedSecret
            const totpCode = getTotpCode(issuer, userEmail, secretKey)
            const qrCodeImageSource = await QRCode.toDataURL(totpCode)
            setQrCode(qrCodeImageSource)
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error: #%d", error)
        } finally {
            setIsLoading(false)
        }
    }, [issuer, userEmail])

    const verifyTotpToken = (): void => {
        setErrorMessage("")
        setIsVerifyingToken(true)
        verifyTOTPSetup({ code: token })
            .then(async () => {
                await updateMFAPreference({ totp: "PREFERRED" })
                onSuccess()
                if (isMfaResetGroup) {
                    restoreUserPermissions()
                }
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e)
                if (/Code mismatch/.test(e.toString())) {
                    setErrorMessage(`Error setting up TOTP - ${e.toString()}`)
                }
            })
            .finally(() => setIsVerifyingToken(false))
    }

    useEffect(() => {
        if (!userEmail) {
            return
        }
        generateQRCode()
    }, [generateQRCode, userEmail])

    const isValidToken = (): boolean => /^\d{6}$/gm.test(token)

    return (
        <FullScreenTitleContent
            subtitle="One More Step ... MFA"
            animateSvg
            content={
                <>
                    <Stack alignItems="center" spacing={5}>
                        <Box>
                            <Typography>
                                You need to set up MFA to access data on Connect
                                Direct. To do this, follow these steps:
                            </Typography>
                            <CheckMarkListWithPrefix
                                items={[
                                    "Download an authenticator app on your phone, such as Google or Microsoft Authenticator",
                                    "Open the app and scan the QR code below",
                                    "The app will produce a code",
                                    "Enter the code in the box below",
                                ]}
                            />
                        </Box>
                        {isLoading ? (
                            <AppSkeleton
                                width={QR_CODE_WIDTH}
                                height={QR_CODE_WIDTH}
                            />
                        ) : (
                            <img
                                src={qrCode}
                                alt="qr code"
                                width={QR_CODE_WIDTH}
                                height={QR_CODE_WIDTH}
                            />
                        )}

                        <TextField
                            label="Auth code"
                            placeholder="Enter the code produced by your authenticator app..."
                            variant="outlined"
                            onKeyDown={(e) => {
                                if (
                                    e.key === "Enter" &&
                                    !e.altKey &&
                                    isValidToken()
                                ) {
                                    verifyTotpToken()
                                }
                            }}
                            onChange={(e) => {
                                setToken(e.target.value)
                            }}
                        />
                        {errorMessage && (
                            <AppAlert
                                sx={{ mt: 2, textAlign: "left" }}
                                variant="filled"
                                severity="warning"
                                onClose={() => {
                                    setErrorMessage("")
                                }}
                            >
                                {errorMessage}
                            </AppAlert>
                        )}
                    </Stack>
                    <DialogActions sx={{ pr: 3, pb: 3 }} />
                </>
            }
            actionContent={
                <Stack spacing={BUTTON_SPACING} width="100%">
                    <AppButton
                        disabled={!isValidToken() || isVerifyingToken}
                        color="primary"
                        variant="contained"
                        type="submit"
                        onClick={verifyTotpToken}
                    >
                        {isVerifyingToken ? "Verifying..." : "Enter code"}
                    </AppButton>
                    <AppButton
                        variant="outlined"
                        type="submit"
                        sx={{ mr: 2 }}
                        onClick={async () => {
                            await signOut({ global: true })
                            scrollToTop(document)
                            if (mode === MFA_MODE_ACCOUNT) {
                                dispatch(setShowAccountSetUp(CLOSED))
                            } else {
                                dispatch(setShowMFA(false))
                            }
                        }}
                    >
                        I&apos;ll do it later, sign me out
                    </AppButton>
                    <UnableToLoginHelp themed />
                </Stack>
            }
        />
    )
}

const CustomSetupTOTP = (): ReactElement => {
    const showMFA: boolean = useGetState(getShowMFA)
    const theme = useTheme()
    const [showSuccessScreen, setShowSuccessScreen] = useState(false)
    const { groups } = useGetUserData()
    const dispatch = useAppDispatch()
    const { signOut } = useAuthenticator((context) => [context.authStatus])

    const isMfaResetGroup = groups.includes("MFA-RESET")

    useEffect(() => {
        setShowSuccessScreen(false)
    }, [])

    useEffect(() => {
        if (showMFA === false) {
            setShowSuccessScreen(false)
        }
    }, [showMFA])

    return (
        <AppDialog
            isOpen={showMFA}
            isFullscreen
            sx={{
                display: "flex",
                justifyContent: "center",
                paddingX: {
                    xs: 1,
                    md: 20,
                    lg: 5,
                },
            }}
        >
            {showSuccessScreen ? (
                <DialogContent>
                    <Stack
                        spacing={4}
                        alignItems="center"
                        paddingX={{ xs: 0, md: 10, lg: 40, xl: 70 }}
                    >
                        <CheckCircleIcon
                            sx={{
                                color: theme.palette.success.main,
                                fontSize: "160px",
                            }}
                        />
                        <Typography variant="h4" fontWeight="500">
                            Success!
                        </Typography>
                        <Typography
                            align="center"
                            variant="h6"
                            fontWeight="normal"
                        >
                            {isMfaResetGroup ? (
                                <>
                                    Your{" "}
                                    <strong>
                                        MFA setup has been successfully
                                        completed.
                                    </strong>{" "}
                                    As your MFA was reset earlier, all your
                                    existing permissions have now been restored.
                                    Please sign out and sign back in to access
                                    the portal.
                                </>
                            ) : (
                                <>
                                    Your account set-up is now complete. It now{" "}
                                    <strong>
                                        needs to be approved by your
                                        organisation&apos;s admin
                                    </strong>{" "}
                                    before you can use it. Please reach out to
                                    them to arrange this.
                                </>
                            )}
                        </Typography>
                        <Typography
                            align="center"
                            variant="h6"
                            fontWeight="normal"
                        >
                            You must sign out and in to complete the process
                        </Typography>
                        <AppButton
                            variant="outlined"
                            color="success"
                            type="submit"
                            onClick={async () => {
                                await signOut({ global: true })
                                scrollToTop(document)
                                dispatch(setShowMFA(false))
                            }}
                            endIcon={<LogoutIcon />}
                        >
                            Close and Sign Out
                        </AppButton>
                    </Stack>
                </DialogContent>
            ) : (
                <MfaPopUp
                    mode={MFA_MODE_MFA_POP_UP}
                    onSuccess={() => {
                        setShowSuccessScreen(true)
                    }}
                />
            )}
        </AppDialog>
    )
}
export default CustomSetupTOTP
